import { CULQI_PUBLIC_KEY } from "@/constants";

/**
 * Checkout de Culqi. Tokeniza la tarjeta en el navegador y devuelve el `tkn_...`
 * para que el backend cree la suscripción: los datos de tarjeta nunca tocan
 * nuestros servidores (requisito PCI).
 */

const CHECKOUT_SCRIPT_URL = "https://js.culqi.com/checkout-js";

type CulqiInstance = {
  open: () => void;
  close: () => void;
  token?: { id: string };
  error?: { user_message?: string; merchant_message?: string };
  culqi: () => void;
  closeCheckout: () => void;
};

declare global {
  // eslint-disable-next-line no-var
  var CulqiCheckout: new (
    publicKey: string,
    config: Record<string, unknown>,
  ) => CulqiInstance;
}

/** Error de tokenización, con el texto que Culqi redacta para el usuario. */
export class CulqiCheckoutError extends Error {
  override name = "CulqiCheckoutError";
}

/** El usuario cerró el checkout sin completar: no hay nada que avisarle. */
export class CulqiCheckoutCancelled extends Error {
  override name = "CulqiCheckoutCancelled";
}

let scriptPromise: Promise<void> | null = null;

/** Carga el script del checkout una sola vez por sesión de navegador. */
function loadCheckoutScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window.CulqiCheckout === "function") return resolve();

    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Se limpia para que un fallo de red no deje el checkout roto para siempre.
      scriptPromise = null;
      reject(new CulqiCheckoutError("No se pudo cargar el checkout de Culqi."));
    };
    document.body.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Abre el checkout y resuelve con el token de la tarjeta. Rechaza si el usuario
 * cierra el modal sin pagar o si Culqi devuelve un error.
 *
 * Solo se ofrece tarjeta: Yape, PagoEfectivo y billeteras no admiten cobros
 * recurrentes, que es lo que crea el backend con este token.
 */
export async function openCulqiCheckout(input: {
  /** En céntimos, tal como lo guarda `plan_priceamount`. */
  amount: number;
  currency: string;
  title: string;
  email: string;
}): Promise<string> {
  if (!CULQI_PUBLIC_KEY) {
    throw new CulqiCheckoutError(
      "Falta configurar la llave pública de Culqi (BUN_PUBLIC_CULQI_PUBLIC_KEY).",
    );
  }

  await loadCheckoutScript();

  return new Promise<string>((resolve, reject) => {
    const culqi = new window.CulqiCheckout(CULQI_PUBLIC_KEY, {
      settings: {
        title: input.title,
        currency: input.currency,
        amount: input.amount,
      },
      client: { email: input.email },
      options: {
        lang: "es",
        modal: true,
        installments: false,
        paymentMethods: {
          tarjeta: true,
          yape: false,
          billetera: false,
          bancaMovil: false,
          agente: false,
          cuotealo: false,
        },
      },
      appearance: { theme: "default", menuType: "sidebar" },
    });

    culqi.culqi = () => {
      if (culqi.token) {
        const tokenId = culqi.token.id;
        culqi.close();
        resolve(tokenId);
        return;
      }

      culqi.close();
      reject(
        new CulqiCheckoutError(
          culqi.error?.user_message ??
            "No pudimos validar tu tarjeta. Inténtalo nuevamente.",
        ),
      );
    };

    // Al cerrar el modal Culqi no llama a `culqi()`: sin esto la promesa queda
    // colgada y quien la espera se queda cargando para siempre.
    culqi.closeCheckout = () => {
      reject(new CulqiCheckoutCancelled("Checkout cerrado por el usuario."));
    };

    culqi.open();
  });
}
